import { isString } from 'lodash'
import { hasField } from './object.no-deps'

/**
 * It can cause problems having a catch error typed as `any` so making an assumtion
 * that typically it's either an Error or string
 */
export type CatchError = Error | string

/**
 * To be used for errors that we don't expect to happen but we need an error for.
 * Useful for being used in a typeguard that loggically should always pass
 */
export const noop = new Error('Unexpected Error (NoOp)')

/**
 * Used to throw from within a `pipe` if the piped value passes a predicate
 *
 *    pipe(
 *        5,
 *        throwIf(isEven, n => new Error(`Expected ${n} to be odd`))
 *    )
 */
export function throwIf<T>(predicate: (t: T) => boolean, makeError: (t: T) => Error) {
    return (t: T) => {
        if (predicate(t)) throw makeError(t)
        return t
    }
}

/**
 * Used to throw from within a `pipe` if the piped value fails a predicate.
 * Can be used with a typeguard to narrow the type of the piped value
 *
 *    pipe(
 *        consoleLog as unknown,
 *        throwUnless(isFunction, () => new Error('Expected a function')),
 *        fn => fn('Hello')
 *    )
 */
export function throwUnless<T, S extends T>(predicate: (t: T) => t is S, makeError: (t: T) => Error): (t: T) => S
export function throwUnless<T>(predicate: (t: T) => boolean, makeError: (t: T) => Error): (t: T) => T
export function throwUnless<T>(predicate: (t: T) => boolean, makeError: (t: T) => Error): (t: T) => T {
    return (t: T) => {
        if (!predicate(t)) throw makeError(t)
        return t
    }
}

export function getErrorMessage(error: CatchError) {
    return hasField(error, 'message') ? error.message : error
}

export function ensureError(error: CatchError | unknown): Error {
    if (error == null) return new Error('Unknown error')
    if (error instanceof Error) return error
    if (isString(error)) return new Error(error)
    return new Error(JSON.stringify(error))
}

/**
 * To be used in a catch block to map the error to a different error
 * and rethrow it. This is useful for mapping errors to a more specific error type
 * or adding additional context to the error.
 */
export function mapError(map: (error: Error) => Error) {
    return (error: CatchError): never => {
        error = ensureError(error)
        throw map(error)
    }
}
